// ============================================================
// Orymia Shared — Common Primitives
// Base types shared across all domains
// ============================================================

// ─── Raw Asset Category ────────────────────────────────────────────────────

export const RAW_ASSET_CATEGORIES = [
  "all",
  "models",
  "textures",
  "soundtracks",
] as const;

export type RawAssetCategory = (typeof RAW_ASSET_CATEGORIES)[number];

// ─── Asset Status Lifecycle ──────────────────────────────────────────────────

/**
 * Asset processing status lifecycle.
 *
 * Governs visibility (disabled assets hidden), processing orchestration
 * (auto-trigger on ingest), and UI presentation (colored status badges).
 *
 * - pending: Asset ingested but not yet processed (no LODs/metadata)
 * - processing: Background job running (LOD generation, texture resize)
 * - ready: Fully processed, available for scene generation
 * - failed: Processing failed, can be retried
 * - disabled: Manually or automatically hidden from users/LLM
 */
export type AssetStatus =
  | "pending"
  | "processing"
  | "ready"
  | "failed"
  | "disabled";

/** Human-readable status labels */
export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  pending: "Pending Processing",
  processing: "Processing...",
  ready: "Ready",
  failed: "Processing Failed",
  disabled: "Disabled",
} as const;

/** Status color mapping for UI (Tailwind classes) */
export const ASSET_STATUS_COLORS: Record<AssetStatus, string> = {
  pending: "bg-amber-500/20 text-amber-600 border-amber-500/30",
  processing: "bg-blue-500/20 text-blue-600 border-blue-500/30",
  ready: "bg-green-500/20 text-green-600 border-green-500/30",
  failed: "bg-red-500/20 text-red-600 border-red-500/30",
  disabled: "bg-gray-500/20 text-gray-600 border-gray-500/30",
} as const;

/**
 * Filter assets by status for production use.
 * Only 'ready' assets should be visible to users and LLM.
 */
export function isAssetReady(status: AssetStatus): boolean {
  return status === "ready";
}

// ─── Primitive Type Aliases ─────────────────────────────────────────────────

/** Object ID — identifies a 3D model asset.
 * Valid IDs are maintained in the server-side OBJECT_REGISTRY. */
export type ObjectId = string;

/** Texture ID — identifies a terrain texture asset.
 * Valid IDs are maintained in the server-side TEXTURE_REGISTRY. */
export type TextureId = string;

/** LOD quality levels for model loading */
export type LODLevel = "high" | "med" | "low" | "thumb";

/** Default LOD level — favors performance */
export const DEFAULT_LOD: LODLevel = "low";

/** 3D position/rotation tuple */
export type Vector3 = [number, number, number];

// ─── Scene Constants ──────────────────────────────────────────────────────

/** HeightMap grid size */
export const HEIGHTMAP_SIZE = 32;

/** Terrain world size (square, in meters/units) */
export const TERRAIN_SIZE = 20;

/** Maximum terrain height (heightMap value 1.0 = this many units) */
export const TERRAIN_HEIGHT_SCALE = 3.0;

/** Maximum objects per scene */
export const MAX_OBJECTS = 500;

/** Maximum light sources per scene */
export const MAX_LIGHTS = 8;

/** Supported Three.js light primitives.
 *  These map directly to Three.js light classes:
 *  - DIRECTIONAL → THREE.DirectionalLight (sun/moon, parallel rays, no decay)
 *  - POINT → THREE.PointLight (bulbs, candles, omnidirectional, decays with distance)
 *  - SPOT → THREE.SpotLight (flashlights, stage lights, cone-shaped beam)
 */
export const LIGHT_TYPES = {
  DIRECTIONAL: "directional",
  POINT: "point",
  SPOT: "spot",
} as const;
export type LightType = (typeof LIGHT_TYPES)[keyof typeof LIGHT_TYPES];

/** Default scene values */
export const DEFAULTS = {
  terrain: {
    color: "#e8dcc4", // soft sand beige
    roughness: 0.9,
  },
  sky: {
    topColor: "#87ceeb",
    bottomColor: "#f0e6d3",
  },
  lighting: {
    ambientIntensity: 0.6,
    hemisphereIntensity: 0.3,
    lights: [
      {
        id: "default-sun",
        type: LIGHT_TYPES.DIRECTIONAL,
        color: "#fff5e6",
        intensity: 1.0,
        position: [20, 40, 20] as Vector3,
        castShadow: true,
      },
    ],
  },
  atmosphere: {
    fogColor: "#e8dcc4",
    fogNear: 20,
    fogFar: 100,
  },
} as const;

// ─── Object Archetypes ─────────────────────────────────────────────────

/**
 * Canonical object archetypes — categories of 3D objects that determine
 * which effects (mesh-level or object-level) apply.
 *
 * Used by:
 *   - Asset manifest: `ObjectAsset.metadata.archetype`
 *   - Space Compiler: resolves archetype → default `ObjectEffects`
 *   - Client MeshEffectManager: resolves archetype → mesh shader drivers
 *   - Client ObjectEffectsRenderer: renders per-object emitters/lights
 */
export const OBJECT_ARCHETYPES = [
  "vegetation",
  "creature",
  "structure",
  "decoration",
  "water",
  "flame",
] as const;
export type ObjectArchetype = (typeof OBJECT_ARCHETYPES)[number];

// ─── Emitter Types ──────────────────────────────────────────────────────

/** Localized particle emitter types for per-object effects. */
export const EMITTER_TYPES = ["fire", "smoke", "steam", "sparks"] as const;
export type EmitterType = (typeof EMITTER_TYPES)[number];
